import React, { useEffect, useMemo, useState } from "react";
import { Calendar, RefreshCw, Building, Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClients,
  updateClientStatus,
} from "../../../features/onboarding/onboardingSlice";
import PageLoader from "../../../components/Common/PageLoader";
import { DataTable } from "../../../components/Common/DataTable";
import { useClientFilter } from "../../../hooks/useClientFilter";
import "./onboarding.scss";

export default function ClientOnboarding({ onClickAddClient }) {
  const dispatch = useDispatch();

  const { clients, clientsStatus, clientsError } = useSelector(
    (state) => state.onboarding
  );

  // Use client filter hook for filtering by selected client
  const { selectedClientId } = useClientFilter();

  // Filter clients by selected client
  const filteredClients = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];
    if (!selectedClientId) return clients; // Return all if no client selected
    
    return clients.filter((client) => client.clientUUID === selectedClientId);
  }, [clients, selectedClientId]);

  useEffect(() => {
    if (clientsStatus === "idle") {
      dispatch(fetchClients());
    }
  }, [dispatch, clientsStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const handleStatusChange = (clientUUID, currentStatus) => {
    dispatch(updateClientStatus({ clientUUID, status: !currentStatus }));
  };

  const handleRefresh = () => {
    dispatch(fetchClients());
  };

  // Transform clients data for the table
  const tableData = useMemo(() => {
    return filteredClients.map((client) => ({
      ...client,
      clientNameDisplay: client.clientName || "Unnamed Client",
      descriptionDisplay: client.description || "No description",
      uuidDisplay: client.clientUUID || "-",
      createdAtDisplay: client.createdAt,
      modifiedAtDisplay: client.modifiedAt,
      isActive: client.status === true,
    }));
  }, [filteredClients]);

  // Define table columns
  const columns = useMemo(
    () => [
      // ===== VISIBLE BY DEFAULT =====
      {
        accessorKey: "clientNameDisplay",
        header: "Client Name",
        size: 200,
        cell: ({ getValue }) => (
          <div className="inline">
            <Building size={14} className="icon-muted" />
            <span className="client-name">{getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: "uuidDisplay",
        header: "Client UUID",
        size: 280,
        cell: ({ getValue }) => (
          <span className="chip chip--muted font-mono small">{getValue()}</span>
        ),
      },
      {
        accessorKey: "descriptionDisplay",
        header: "Description",
        size: 250,
        cell: ({ getValue }) => {
          const desc = getValue();
          const truncated =
            desc && desc.length > 60 ? desc.substring(0, 60) + "..." : desc;
          return (
            <span className="text-muted" title={desc}>
              {truncated}
            </span>
          );
        },
      },
      {
        accessorKey: "createdAtDisplay",
        header: "Created Date",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Calendar size={12} className="icon-muted" />
            <span className="font-mono small">{formatDate(getValue())}</span>
          </div>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 120,
        cell: ({ row }) => {
          const client = row.original;
          const isActive = client.isActive;

          return (
            <label
              className="status-toggle"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={() => handleStatusChange(client.clientUUID, isActive)}
              />
              <span className="status-toggle__slider"></span>
              <span className="status-toggle__label">
                {isActive ? "Active" : "Inactive"}
              </span>
            </label>
          );
        },
      },

      // ===== HIDDEN BY DEFAULT =====
      {
        accessorKey: "modifiedAtDisplay",
        header: "Modified Date",
        size: 160,
        cell: ({ getValue }) => (
          <div className="inline">
            <Calendar size={12} className="icon-muted" />
            <span className="font-mono small">{formatDate(getValue())}</span>
          </div>
        ),
      },
    ],
    []
  );

  // Initial column visibility
  const initialColumnVisibility = useMemo(
    () => ({
      modifiedAtDisplay: false,
    }),
    []
  );

  if (clientsStatus === "loading") {
    return <PageLoader loading={true} />;
  }

  return (
    <div className="client-onboarding">
      {/* Header */}
      <div className="client-onboarding__header">
        <div>
          <h2 className="client-onboarding__title">Client Onboarding</h2>
          <p className="client-onboarding__subtitle">
            Manage clients and their onboarding status
          </p>
          {clientsError && (
            <div className="api-status">
              <span className="api-status__error">❌ {clientsError}</span>
            </div>
          )}
        </div>
        <div className="client-onboarding__actions">
          <button
            className="btn btn--outline"
            onClick={handleRefresh}
            disabled={clientsStatus === "loading"}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="btn btn--primary" onClick={onClickAddClient}>
            <Plus size={16} />
            Add New Client
          </button>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        data={tableData}
        columns={columns}
        enableSearch={true}
        enableFilters={true}
        enableColumnVisibility={true}
        enableSorting={true}
        enablePagination={true}
        enableExport={true}
        enableDensity={true}
        pageSize={10}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        emptyMessage="No clients found. Create your first client to get started."
        loading={clientsStatus === "loading"}
        initialColumnVisibility={initialColumnVisibility}
      />

      {/* Summary Stats */}
      <div className="client-onboarding__stats">
        <div className="stat-item">
          <span className="stat-item__label">Total Clients:</span>
          <span className="stat-item__value">{filteredClients.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Active Clients:</span>
          <span className="stat-item__value">
            {filteredClients.filter((c) => c.status === true).length}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-item__label">Inactive Clients:</span>
          <span className="stat-item__value">
            {filteredClients.filter((c) => c.status !== true).length}
          </span>
        </div>
      </div>
    </div>
  );
}
